import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../App';
import Layout from '../components/Layout';
import { useTranslation } from 'react-i18next';
import { FileText, Search, Plus, Upload, Eye, Calendar, Tag, UploadCloud, ChevronRight, ChevronDown, FolderOpen, Grid, List as ListIcon, FolderTree, Star, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { useDropzone } from 'react-dropzone';

// Helper function to strip HTML tags and get clean text
const stripHtml = (html) => {
  if (!html) return '';
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent || '';
};

const DocumentLibrary = ({ user, setUser }) => {
  const { t } = useTranslation();
  const [documents, setDocuments] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedTag, setSelectedTag] = useState('');
  const [categories, setCategories] = useState([]);
  const [categoryTree, setCategoryTree] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadTitle, setUploadTitle] = useState('');
  const [uploadCategory, setUploadCategory] = useState('');
  const [uploadTags, setUploadTags] = useState('');
  const [uploading, setUploading] = useState(false);
  const [bulkUploadDialogOpen, setBulkUploadDialogOpen] = useState(false);
  const [bulkFiles, setBulkFiles] = useState([]);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [selectedDocs, setSelectedDocs] = useState([]);
  const [bulkCategoryDialogOpen, setBulkCategoryDialogOpen] = useState(false);
  const [bulkCategory, setBulkCategory] = useState('');
  const [viewMode, setViewMode] = useState('tree'); // 'grid', 'list', 'tree'
  const [sortBy, setSortBy] = useState('updated_desc'); // updated_desc | updated_asc | views_desc | title_asc | created_desc
  const [expandedCategories, setExpandedCategories] = useState({});

  const onDrop = (acceptedFiles) => {
    setBulkFiles(acceptedFiles);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'text/plain': ['.txt'],
      'text/markdown': ['.md'],
      'text/csv': ['.csv'],
      'text/javascript': ['.js', '.jsx'],
      'text/typescript': ['.ts', '.tsx'],
      'text/x-python': ['.py']
    }
  });

  useEffect(() => {
    fetchDocuments();
    fetchCategories();
    fetchTags();
  }, [selectedCategory, selectedTag, searchQuery]);

  const fetchDocuments = async () => {
    try {
      const params = new URLSearchParams();
      if (selectedCategory) params.append('category', selectedCategory);
      if (selectedTag) params.append('tag', selectedTag);
      if (searchQuery) params.append('search', searchQuery);
      
      const res = await axios.get(`${API_URL}/documents?${params.toString()}`);
      setDocuments(res.data);
    } catch (error) {
      toast.error('Failed to fetch documents');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      // Use regular categories endpoint
      const res = await axios.get(`${API_URL}/categories`);
      const cats = res.data || [];
      setCategories(cats);
      setCategoryTree(buildCategoryTree(cats));
      // Auto-expand all categories
      const expanded = {};
      cats.forEach(cat => {
        expanded[cat.id] = true;
      });
      setExpandedCategories(expanded);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const buildCategoryTree = (cats) => {
    const catMap = {};
    cats.forEach(cat => {
      catMap[cat.id] = { ...cat, children: [], level: 0 };
    });

    cats.forEach(cat => {
      let level = 0;
      let current = catMap[cat.id];
      while (current.parent_id && catMap[current.parent_id]) {
        level++;
        current = catMap[current.parent_id];
      }
      catMap[cat.id].level = level;
    });

    const tree = [];
    Object.values(catMap).forEach(cat => {
      if (cat.parent_id && catMap[cat.parent_id]) {
        catMap[cat.parent_id].children.push(cat);
      } else {
        tree.push(cat);
      }
    });

    return tree;
  };

  const fetchTags = async () => {
    try {
      const res = await axios.get(`${API_URL}/tags`);
      setTags(res.data.tags || []);
    } catch (error) {
      console.error('Error fetching tags:', error);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadFile || !uploadTitle) {
      toast.error('Please provide a file and title');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('title', uploadTitle);
      if (uploadCategory) formData.append('category', uploadCategory);
      if (uploadTags) formData.append('tags', uploadTags);

      await axios.post(`${API_URL}/documents/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success('Document uploaded successfully!');
      setUploadDialogOpen(false);
      setUploadFile(null);
      setUploadTitle('');
      setUploadCategory('');
      setUploadTags('');
      fetchDocuments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleBulkUpload = async () => {
    if (bulkFiles.length === 0) {
      toast.error('Please select files to upload');
      return;
    }

    setBulkUploading(true);
    try {
      const formData = new FormData();
      bulkFiles.forEach(file => {
        formData.append('files', file);
      });
      if (uploadCategory) formData.append('category', uploadCategory);
      if (uploadTags) formData.append('tags', uploadTags);
      formData.append('visibility', 'team');

      const res = await axios.post(`${API_URL}/documents/bulk-upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success(`Successfully uploaded ${res.data.successful} documents!`);
      if (res.data.failed > 0) {
        toast.error(`Failed to upload ${res.data.failed} documents`);
      }
      
      setBulkUploadDialogOpen(false);
      setBulkFiles([]);
      setUploadCategory('');
      setUploadTags('');
      fetchDocuments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Bulk upload failed');
    } finally {
      setBulkUploading(false);
    }
  };

  const toggleDocSelection = (docId) => {
    setSelectedDocs(prev => 
      prev.includes(docId) ? prev.filter(id => id !== docId) : [...prev, docId]
    );
  };

  const toggleStar = async (docId, e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      const res = await axios.put(`${API_URL}/documents/${docId}/star`);
      setDocuments(prev => prev.map(doc => 
        doc.id === docId ? { ...doc, starred: res.data.starred } : doc
      ));
      toast.success(res.data.starred ? 'Document starred!' : 'Star removed');
    } catch (error) {
      toast.error('Failed to update star');
    }
  };
  const toggleSelectAll = () => {
    if (selectedDocs.length === documents.length) {
      setSelectedDocs([]);
    } else {
      setSelectedDocs(documents.map(doc => doc.id));
    }
  };

  const handleQuickCategoryChange = async (docId, newCategory) => {
    try {
      await axios.put(`${API_URL}/documents/${docId}`, {
        category: newCategory || null
      });
      toast.success('Category updated!');
      fetchDocuments();
    } catch (error) {
      toast.error('Failed to update category');
    }
  };

  const handleBulkCategoryChange = async () => {
    if (selectedDocs.length === 0) {
      toast.error('No documents selected');
      return;
    }

    try {
      await Promise.all(
        selectedDocs.map(docId => 
          axios.put(`${API_URL}/documents/${docId}`, {
            category: bulkCategory || null
          })
        )
      );
      toast.success(`Updated ${selectedDocs.length} documents!`);
      setBulkCategoryDialogOpen(false);
      setSelectedDocs([]);
      setBulkCategory('');
      fetchDocuments();
    } catch (error) {
      toast.error('Failed to update categories');
    }
  };

  const handleBulkDelete = async () => {
    if (selectedDocs.length === 0) {
      toast.error('No documents selected');
      return;
    }

    if (!confirm(`Delete ${selectedDocs.length} documents? This cannot be undone.`)) {
      return;
    }

    try {
      await Promise.all(
        selectedDocs.map(docId => axios.delete(`${API_URL}/documents/${docId}`))
      );
      toast.success(`Deleted ${selectedDocs.length} documents!`);
      setSelectedDocs([]);
      fetchDocuments();
    } catch (error) {
      toast.error('Failed to delete documents');
    }
  };

  const toggleCategory = (catId) => {
    setExpandedCategories(prev => ({ ...prev, [catId]: !prev[catId] }));
  };

  // Get documents for a specific category
  const getDocsForCategory = (categoryName) => {
    return documents.filter(doc => doc.category === categoryName);
  };

  // Get uncategorized documents
  const getUncategorizedDocs = () => {
    return documents.filter(doc => !doc.category);
  };

  // Render document card
  const renderDocCard = (doc, compact = false) => (
    <div
      key={doc.id}
      className={`bg-white border border-zinc-200 rounded-lg ${compact ? 'p-4' : 'p-6'} hover:border-violet-300 hover:shadow-sm transition-all duration-200 group relative`}
      data-testid={`document-card-${doc.id}`}
    >
      {/* Checkbox */}
      <div className="absolute top-3 left-3 z-10">
        <input
          type="checkbox"
          checked={selectedDocs.includes(doc.id)}
          onChange={() => toggleDocSelection(doc.id)}
          className="h-4 w-4 rounded border-zinc-300"
          onClick={(e) => e.stopPropagation()}
          data-testid={`checkbox-${doc.id}`}
        />
      </div>

      {/* Star Button */}
      <button
        onClick={(e) => toggleStar(doc.id, e)}
        className="absolute top-3 right-3 z-10 p-1 rounded hover:bg-yellow-50 transition-colors"
        title={doc.starred ? 'Unstar document' : 'Star document'}
      >
        <Star 
          className={`h-5 w-5 ${doc.starred ? 'fill-yellow-400 text-yellow-400' : 'text-zinc-300 hover:text-yellow-400'}`} 
        />
      </button>

      <Link to={`/documents/${doc.id}`} className="block">
        <div className={`flex items-start justify-between ${compact ? 'mb-2 pl-6' : 'mb-4 pl-8'}`}>
          <div className={`${compact ? 'h-8 w-8' : 'h-12 w-12'} rounded-lg bg-violet-100 flex items-center justify-center group-hover:bg-violet-200 transition-colors duration-200`}>
            <FileText className={`${compact ? 'h-4 w-4' : 'h-6 w-6'} text-violet-600`} />
          </div>
          <div className="flex items-center gap-2">
            {doc.starred && (
              <span className="text-xs text-yellow-600 font-medium">★ Featured</span>
            )}
            {doc.file_type && (
              <span className="px-2 py-1 text-xs font-mono bg-zinc-100 text-zinc-600 rounded">
                .{doc.file_type}
              </span>
            )}
          </div>
        </div>
        <h3 className={`${compact ? 'text-sm' : 'text-lg'} font-bold text-zinc-900 mb-2 line-clamp-2`} style={{ fontFamily: 'Manrope, sans-serif' }}>
          {doc.title}
        </h3>
        <p className={`${compact ? 'text-xs' : 'text-sm'} text-zinc-600 mb-3 line-clamp-2`}>
          {stripHtml(doc.content).substring(0, compact ? 80 : 120)}...
        </p>
        <div className={`flex items-center gap-3 ${compact ? 'text-xs' : 'text-xs'} text-zinc-500`}>
          <div className="flex items-center gap-1">
            <Eye className="h-3 w-3" />
            <span>{doc.views}</span>
          </div>
          <div className="flex items-center gap-1">
            <Calendar className="h-3 w-3" />
            <span>{new Date(doc.created_at).toLocaleDateString()}</span>
          </div>
        </div>
      </Link>

      {/* Quick Category Selector */}
      {!compact && (
        <div className="mt-4 pt-4 border-t border-zinc-200" onClick={(e) => e.stopPropagation()}>
          <Label className="text-xs text-zinc-600 mb-1 block">Category</Label>
          <select
            value={doc.category || ''}
            onChange={(e) => handleQuickCategoryChange(doc.id, e.target.value)}
            className="w-full h-8 px-2 text-sm rounded-md border border-zinc-200 bg-white"
            data-testid={`category-select-${doc.id}`}
          >
            <option value="">No Category</option>
            {categories.map(cat => (
              <option key={cat.id} value={cat.name}>
                {'  '.repeat(cat.level || 0)}{cat.name}
              </option>
            ))}
          </select>
        </div>
      )}

      {doc.tags && doc.tags.length > 0 && !compact && (
        <div className="flex flex-wrap gap-2 mt-3">
          {doc.tags.slice(0, 3).map((tag, idx) => (
            <span key={idx} className="inline-flex items-center px-2 py-1 rounded-md bg-zinc-100 text-xs text-zinc-600">
              <Tag className="h-3 w-3 mr-1" />
              {tag}
            </span>
          ))}
        </div>
      )}
    </div>
  );

  // Render category tree with documents
  const renderCategoryTree = (cats, level = 0) => {
    return cats.map(cat => {
      const catDocs = getDocsForCategory(cat.name);
      const hasChildren = cat.children && cat.children.length > 0;
      const isExpanded = expandedCategories[cat.id];
      const hasContent = catDocs.length > 0 || hasChildren;

      return (
        <div key={cat.id} className="mb-2">
          {/* Category Header */}
          <div
            className={`flex items-center gap-2 p-3 rounded-lg cursor-pointer transition-colors ${
              selectedCategory === cat.name
                ? 'bg-violet-100 border border-violet-300'
                : 'bg-zinc-50 hover:bg-zinc-100 border border-transparent'
            }`}
            style={{ marginLeft: `${level * 1.5}rem` }}
            onClick={() => {
              toggleCategory(cat.id);
              setSelectedCategory(selectedCategory === cat.name ? '' : cat.name);
            }}
          >
            <button className="p-0.5" onClick={(e) => { e.stopPropagation(); toggleCategory(cat.id); }}>
              {isExpanded ? (
                <ChevronDown className="h-4 w-4 text-zinc-500" />
              ) : (
                <ChevronRight className="h-4 w-4 text-zinc-500" />
              )}
            </button>
            <FolderOpen className={`h-5 w-5 ${level === 0 ? 'text-violet-600' : level === 1 ? 'text-teal-600' : 'text-amber-600'}`} />
            <span className="font-semibold text-zinc-900 flex-1">{cat.name}</span>
            <span className="text-xs bg-zinc-200 text-zinc-600 px-2 py-0.5 rounded-full">
              {catDocs.length}
            </span>
            <Link
              to="/documents/new"
              state={{ category: cat.name }}
              className="p-1 rounded hover:bg-violet-200 text-violet-600"
              onClick={(e) => e.stopPropagation()}
              title="Create document in this category"
            >
              <Plus className="h-4 w-4" />
            </Link>
          </div>

          {/* Documents in this category */}
          {isExpanded && catDocs.length > 0 && (
            <div
              className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2 mb-3"
              style={{ marginLeft: `${(level + 1) * 1.5 + 0.5}rem` }}
            >
              {catDocs.map(doc => renderDocCard(doc, true))}
            </div>
          )}

          {/* Child categories */}
          {isExpanded && hasChildren && (
            <div className="mt-1">
              {renderCategoryTree(cat.children, level + 1)}
            </div>
          )}
        </div>
      );
    });
  };

  // Sort documents client-side based on sortBy
  const sortedDocs = React.useMemo(() => {
    const arr = [...documents];
    switch (sortBy) {
      case 'updated_asc': arr.sort((a, b) => new Date(a.updated_at) - new Date(b.updated_at)); break;
      case 'created_desc': arr.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)); break;
      case 'views_desc': arr.sort((a, b) => (b.views || 0) - (a.views || 0)); break;
      case 'title_asc': arr.sort((a, b) => (a.title || '').localeCompare(b.title || '')); break;
      case 'updated_desc':
      default: arr.sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));
    }
    return arr;
  }, [documents, sortBy]);

  return (
    <Layout user={user} setUser={setUser}>
      <div className="p-8" data-testid="document-library">
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-4xl font-bold text-zinc-900" style={{ fontFamily: 'Manrope, sans-serif', letterSpacing: '-0.02em' }}>
              {t('documents')}
            </h1>
            <div className="flex gap-3">
              {/* View Mode Toggle */}
              <div className="flex gap-1 bg-zinc-100 p-1 rounded-lg">
                <button
                  onClick={() => setViewMode('tree')}
                  className={`p-2 rounded ${viewMode === 'tree' ? 'bg-white shadow-sm' : 'hover:bg-zinc-200'}`}
                  title="Tree View"
                  data-testid="tree-view-button"
                >
                  <FolderTree className="h-4 w-4" />
                </button>
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 rounded ${viewMode === 'grid' ? 'bg-white shadow-sm' : 'hover:bg-zinc-200'}`}
                  title="Grid View"
                  data-testid="grid-view-button"
                >
                  <Grid className="h-4 w-4" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded ${viewMode === 'list' ? 'bg-white shadow-sm' : 'hover:bg-zinc-200'}`}
                  title="List View"
                  data-testid="list-view-button"
                >
                  <ListIcon className="h-4 w-4" />
                </button>
              </div>

              <select
                value={sortBy}
                onChange={e => setSortBy(e.target.value)}
                className="h-10 px-3 rounded-lg border border-zinc-200 bg-white text-sm font-medium"
                data-testid="sort-select"
                title="Sort articles"
              >
                <option value="updated_desc">Recently Updated</option>
                <option value="updated_asc">Oldest Update</option>
                <option value="created_desc">Newest First</option>
                <option value="views_desc">Most Viewed</option>
                <option value="title_asc">A → Z</option>
              </select>

              <Dialog open={bulkUploadDialogOpen} onOpenChange={setBulkUploadDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-zinc-600 hover:bg-zinc-700" data-testid="bulk-upload-button">
                    <UploadCloud className="h-4 w-4 mr-2" />
                    Bulk Upload
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl">
                  <DialogHeader>
                    <DialogTitle>Bulk Upload Documents</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div
                      {...getRootProps()}
                      className={`border-2 border-dashed rounded-lg p-12 text-center cursor-pointer transition-colors ${
                        isDragActive ? 'border-violet-500 bg-violet-50' : 'border-zinc-300 hover:border-violet-400'
                      }`}
                      data-testid="bulk-dropzone"
                    >
                      <input {...getInputProps()} />
                      <UploadCloud className="h-12 w-12 mx-auto mb-4 text-zinc-400" />
                      {isDragActive ? (
                        <p className="text-zinc-700 font-medium">Drop files here...</p>
                      ) : (
                        <div>
                          <p className="text-zinc-700 font-medium mb-2">Drag & drop files here, or click to select</p>
                          <p className="text-sm text-zinc-500">Supports PDF, DOCX, XLSX, TXT, MD, CSV, and code files</p>
                        </div>
                      )}
                    </div>
                    
                    {bulkFiles.length > 0 && (
                      <div className="max-h-48 overflow-y-auto border border-zinc-200 rounded-lg p-4">
                        <div className="text-sm font-semibold text-zinc-900 mb-2">
                          Selected Files ({bulkFiles.length})
                        </div>
                        <div className="space-y-2">
                          {bulkFiles.map((file, index) => (
                            <div key={index} className="flex items-center justify-between text-sm">
                              <span className="text-zinc-700">{file.name}</span>
                              <span className="text-zinc-500">{(file.size / 1024).toFixed(2)} KB</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <div>
                      <Label>Category (Optional)</Label>
                      <select
                        value={uploadCategory}
                        onChange={(e) => setUploadCategory(e.target.value)}
                        className="w-full h-10 px-3 mt-2 rounded-md border border-zinc-200 bg-white text-sm"
                        data-testid="bulk-category-select"
                      >
                        <option value="">No Category</option>
                        {categories.map(cat => (
                          <option key={cat.id} value={cat.name}>
                            {'  '.repeat(cat.level || 0)}{cat.level > 0 ? '└─ ' : ''}{cat.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <Label>Tags (Optional, comma-separated)</Label>
                      <Input
                        value={uploadTags}
                        onChange={(e) => setUploadTags(e.target.value)}
                        placeholder="e.g. api, guide"
                        className="mt-2"
                        data-testid="bulk-tags-input"
                      />
                    </div>

                    <Button
                      onClick={handleBulkUpload}
                      className="w-full"
                      disabled={bulkUploading || bulkFiles.length === 0}
                      data-testid="submit-bulk-upload-button"
                    >
                      {bulkUploading ? `Uploading ${bulkFiles.length} files...` : `Upload ${bulkFiles.length} Files`}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>

              <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-teal-600 hover:bg-teal-700" data-testid="upload-document-button">
                    <Upload className="h-4 w-4 mr-2" />
                    Upload File
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Upload Document</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleUpload} className="space-y-4">
                    <div>
                      <Label>File</Label>
                      <Input
                        type="file"
                        onChange={(e) => setUploadFile(e.target.files[0])}
                        accept=".pdf,.docx,.doc,.xlsx,.xls,.txt,.md,.csv,.py,.js,.jsx,.ts,.tsx"
                        required
                        className="mt-2"
                        data-testid="file-input"
                      />
                    </div>
                    <div>
                      <Label>Title</Label>
                      <Input
                        value={uploadTitle}
                        onChange={(e) => setUploadTitle(e.target.value)}
                        placeholder="Document title"
                        required
                        className="mt-2"
                        data-testid="upload-title-input"
                      />
                    </div>
                    <div>
                      <Label>Category (Optional)</Label>
                      <select
                        value={uploadCategory}
                        onChange={(e) => setUploadCategory(e.target.value)}
                        className="w-full h-10 px-3 mt-2 rounded-md border border-zinc-200 bg-white text-sm"
                        data-testid="upload-category-select"
                      >
                        <option value="">No Category</option>
                        {categories.map(cat => (
                          <option key={cat.id} value={cat.name}>
                            {'  '.repeat(cat.level || 0)}{cat.level > 0 ? '└─ ' : ''}{cat.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <Label>Tags (Optional, comma-separated)</Label>
                      <Input
                        value={uploadTags}
                        onChange={(e) => setUploadTags(e.target.value)}
                        placeholder="e.g. api, documentation"
                        className="mt-2"
                        data-testid="upload-tags-input"
                      />
                    </div>
                    <Button type="submit" className="w-full" disabled={uploading} data-testid="upload-submit-button">
                      {uploading ? 'Uploading...' : 'Upload'}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>

              <Link to="/ai-studio">
                <Button className="bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:opacity-90 text-white" data-testid="ai-create-button">
                  <Sparkles className="h-4 w-4 mr-2" />
                  AI Create
                </Button>
              </Link>

              <Link to="/categories">
                <Button variant="outline" data-testid="manage-categories-button">
                  <FolderTree className="h-4 w-4 mr-2" />
                  Manage Categories
                </Button>
              </Link>

              <Link to="/documents/new">
                <Button className="bg-violet-600 hover:bg-violet-700" data-testid="create-new-button">
                  <Plus className="h-4 w-4 mr-2" />
                  {t('create')}
                </Button>
              </Link>
            </div>
          </div>

          {/* Search and Filters */}
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-zinc-400" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t('search') + '...'}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
            {viewMode !== 'tree' && (
              <>
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="h-10 px-4 rounded-md border border-zinc-200 bg-white text-sm"
                  data-testid="category-filter"
                >
                  <option value="">All Categories</option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.name}>
                      {'  '.repeat(cat.level || 0)}{cat.name}
                    </option>
                  ))}
                </select>
                <select
                  value={selectedTag}
                  onChange={(e) => setSelectedTag(e.target.value)}
                  className="h-10 px-4 rounded-md border border-zinc-200 bg-white text-sm"
                  data-testid="tag-filter"
                >
                  <option value="">All Tags</option>
                  {tags.map(tag => (
                    <option key={tag} value={tag}>{tag}</option>
                  ))}
                </select>
              </>
            )}
          </div>

          {/* Bulk Actions Toolbar */}
          {selectedDocs.length > 0 && (
            <div className="sticky top-4 z-20 mt-4 bg-violet-50 border border-violet-200 rounded-lg p-4 flex items-center justify-between shadow-md" data-testid="bulk-actions-toolbar">
              <div className="flex items-center gap-3">
                <span className="text-sm font-semibold text-violet-900">
                  {selectedDocs.length} document{selectedDocs.length > 1 ? 's' : ''} selected
                </span>
                <button
                  onClick={() => setSelectedDocs([])}
                  className="text-sm text-violet-600 hover:text-violet-700"
                >
                  Clear selection
                </button>
              </div>
              <div className="flex gap-2">
                <Dialog open={bulkCategoryDialogOpen} onOpenChange={setBulkCategoryDialogOpen}>
                  <DialogTrigger asChild>
                    <Button variant="outline" size="sm" data-testid="bulk-change-category">
                      Change Category
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Change Category for {selectedDocs.length} Documents</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div>
                        <Label>Select Category</Label>
                        <select
                          value={bulkCategory}
                          onChange={(e) => setBulkCategory(e.target.value)}
                          className="w-full h-10 px-3 mt-2 rounded-md border border-zinc-200 bg-white text-sm"
                          data-testid="bulk-category-select"
                        >
                          <option value="">No Category</option>
                          {categories.map(cat => (
                            <option key={cat.id} value={cat.name}>
                              {'  '.repeat(cat.level || 0)}{cat.level > 0 ? '└─ ' : ''}{cat.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <Button
                        onClick={handleBulkCategoryChange}
                        className="w-full"
                        data-testid="confirm-bulk-category"
                      >
                        Update {selectedDocs.length} Documents
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleBulkDelete}
                  className="text-red-600 hover:bg-red-50"
                  data-testid="bulk-delete"
                >
                  Delete Selected
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Content based on view mode */}
        {loading ? (
          <div className="text-center py-12 text-zinc-500">{t('loading')}</div>
        ) : viewMode === 'tree' ? (
          /* Tree View */
          <div className="space-y-2" data-testid="tree-view">
            {/* Render category tree */}
            {categoryTree.length > 0 && renderCategoryTree(categoryTree)}

            {/* Uncategorized documents */}
            {getUncategorizedDocs().length > 0 && (
              <div className="mt-4">
                <div
                  className="flex items-center gap-2 p-3 rounded-lg bg-zinc-100 cursor-pointer"
                  onClick={() => setExpandedCategories(prev => ({ ...prev, uncategorized: !prev.uncategorized }))}
                >
                  <button className="p-0.5">
                    {expandedCategories.uncategorized ? (
                      <ChevronDown className="h-4 w-4 text-zinc-500" />
                    ) : (
                      <ChevronRight className="h-4 w-4 text-zinc-500" />
                    )}
                  </button>
                  <FolderOpen className="h-5 w-5 text-zinc-500" />
                  <span className="font-semibold text-zinc-700 flex-1">Uncategorized</span>
                  <span className="text-xs bg-zinc-200 text-zinc-600 px-2 py-0.5 rounded-full">
                    {getUncategorizedDocs().length}
                  </span>
                </div>
                {expandedCategories.uncategorized && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2 ml-6">
                    {getUncategorizedDocs().map(doc => renderDocCard(doc, true))}
                  </div>
                )}
              </div>
            )}

            {categoryTree.length === 0 && documents.length === 0 && (
              <div className="text-center py-12" data-testid="empty-state">
                <FileText className="h-16 w-16 mx-auto mb-4 text-zinc-300" />
                <h3 className="text-lg font-semibold text-zinc-900 mb-2">{t('noDocuments')}</h3>
                <p className="text-zinc-600 mb-6">{t('createFirstDocument')}</p>
                <Link to="/documents/new">
                  <Button className="bg-violet-600 hover:bg-violet-700">
                    <Plus className="h-4 w-4 mr-2" />
                    {t('newDocument')}
                  </Button>
                </Link>
              </div>
            )}
          </div>
        ) : documents.length > 0 ? (
          /* Grid/List View */
          <>
            {/* Select All Checkbox */}
            <div className="flex items-center gap-2 mb-4">
              <input
                type="checkbox"
                checked={selectedDocs.length === documents.length && documents.length > 0}
                onChange={toggleSelectAll}
                className="h-4 w-4 rounded border-zinc-300"
                data-testid="select-all-checkbox"
              />
              <label className="text-sm text-zinc-600">
                Select all {documents.length} documents
              </label>
            </div>

            <div className={viewMode === 'grid' 
              ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" 
              : "space-y-4"
            } data-testid="documents-view">
              {sortedDocs.map((doc) => renderDocCard(doc, viewMode === 'list'))}
            </div>
          </>
        ) : (
          <div className="text-center py-12" data-testid="empty-state">
            <FileText className="h-16 w-16 mx-auto mb-4 text-zinc-300" />
            <h3 className="text-lg font-semibold text-zinc-900 mb-2">{t('noDocuments')}</h3>
            <p className="text-zinc-600 mb-6">{t('createFirstDocument')}</p>
            <Link to="/documents/new">
              <Button className="bg-violet-600 hover:bg-violet-700">
                <Plus className="h-4 w-4 mr-2" />
                {t('newDocument')}
              </Button>
            </Link>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default DocumentLibrary;
